/**
 * @file useHeldAttachmentPushes.ts
 * @description The signed-in user's held pushes under one artifact — the
 * server's own account of desktop-agent saves it refused. Consumer:
 * `AttachmentHeldPushAlerts`, which opens this itself rather than taking a prop
 * from a host, the way `DesktopKeepBanner` does, so no host has to know the
 * feature exists.
 *
 * The `userId` narrowing is in the query, not after it: a held push is one
 * person's machine failing to send, and another person's rights are not this
 * reader's business. A bare equality on one field is auto-indexed, so
 * `firestore.indexes.json` needs no entry for it.
 *
 * `createCollectionStore` only consults the registry, so the path has to be
 * `connect()`ed here — a store built and never connected is an orphan whose
 * state never updates and whose `delete` throws. An artifact path that names no
 * document, and a signed-out reader, both fall to the inert store, so no
 * listener opens and `dismiss` is a no-op.
 *
 * Deleting the record is the dismissal, and it is the only write the rules
 * permit. It is undebounced because the reader is watching the alert go, and a
 * fresh refusal writes the record again on purpose: the record is a live status,
 * not a notification.
 */

import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import {
    HELD_PUSHES_COLLECTION,
    heldAttachmentPushId,
    type HeldAttachmentPush,
} from '#types';
import { UserContext } from '../../providers/UserContext';
import { createCollectionStore, type CollectionStore } from '../../store';

export type HeldAttachmentPushesState = {
    /** This reader's held pushes under the artifact. Empty while loading. */
    pushes: readonly HeldAttachmentPush[];
    loading: boolean;
    /** Deletes this reader's records for these files. Never a create or update. */
    dismiss: (fileUids: readonly string[]) => Promise<void>;
};

const NO_PUSHES: readonly HeldAttachmentPush[] = [];

const inactiveStore = createCollectionStore<HeldAttachmentPush>('__inactive__');

/** The subcollection under an artifact path, or null for anything that is not one. */
const heldPushesPath = (artifactPath: string | null | undefined): string | null => {
    if (!artifactPath) {
        return null;
    }
    const segments = artifactPath.split('/');
    if (segments.length % 2 !== 0 || segments.some((segment) => segment.length === 0)) {
        return null;
    }
    return `${artifactPath}/${HELD_PUSHES_COLLECTION}`;
};

export const useHeldAttachmentPushes = (
    artifactPath: string | null | undefined,
): HeldAttachmentPushesState => {
    const { user } = useContext(UserContext);
    const uid = user?.uid;
    const path = heldPushesPath(artifactPath);

    const store = useMemo<CollectionStore<HeldAttachmentPush>>(
        () => path !== null && uid !== undefined
            ? createCollectionStore<HeldAttachmentPush>(path, {
                queryOptions: {
                    extraConstraints: [
                        { type: 'where', field: 'userId', op: '==', value: uid },
                    ],
                },
            })
            : inactiveStore,
        [path, uid],
    );

    useEffect(() => {
        if (path === null || uid === undefined) return;
        store.connect();
        return () => store.disconnect();
    }, [path, uid, store]);

    const snap = useSnapshot(store.state);

    const pushes = useMemo<readonly HeldAttachmentPush[]>(() => {
        if (path === null || uid === undefined) return NO_PUSHES;

        return snap.docIds.flatMap((docId) => {
            const push = snap.docs.get(docId)?.data as HeldAttachmentPush | undefined;
            return push ? [push] : [];
        });
    }, [path, uid, snap.docIds, snap.docs]);

    const dismiss = useCallback(async (fileUids: readonly string[]) => {
        if (path === null || uid === undefined) return;

        await Promise.all(fileUids.map((fileUid) =>
            store.delete(heldAttachmentPushId(uid, fileUid), { debounce: false })));
    }, [path, uid, store]);

    return {
        pushes,
        loading: path !== null && uid !== undefined ? snap.loading : false,
        dismiss,
    };
};
